import React from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';

const CRMActionConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const actionType = node.data.config.actionType || 'create_customer';

  const actions = [
    { value: 'create_customer', label: 'Ügyfél létrehozás', icon: '👤' },
    { value: 'update_customer', label: 'Ügyfél frissítés', icon: '✏️' },
    { value: 'get_customer', label: 'Ügyfél lekérdezés', icon: '🔍' },
    { value: 'delete_customer', label: 'Ügyfél törlés', icon: '🗑️' },
    { value: 'create_project', label: 'Projekt létrehozás', icon: '📁' },
    { value: 'create_task', label: 'Feladat létrehozás', icon: '📋' }
  ];

  return (
    <div className="space-y-3">
      {/* CRM Action Cards */}
      <div>
        <label className={commonStyles.smallLabel}>
          CRM Művelet
        </label>
        <div className="grid grid-cols-2 gap-2">
          {actions.map((action) => (
            <button
              key={action.value}
              onClick={() => updateConfig('actionType', action.value)}
              className={`flex flex-col items-center justify-center p-3 rounded-md border text-sm transition-all min-h-[80px] ${
                actionType === action.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              <span className="text-2xl mb-1">{action.icon}</span>
              <span className="font-medium text-xs text-center leading-tight">{action.label}</span>
              {actionType === action.value && (
                <span className="text-primary-500 mt-1">✓</span>
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Action Settings */}
      {actionType && (
        <div className={commonStyles.card}>
          <div className="flex items-center justify-between mb-2">
            <label className={commonStyles.smallLabel}>
              Művelet beállítások
            </label>
            <button className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
              Részletek
            </button>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Adatforrás:</span>
              <select 
                value={node.data.config.dataSource || 'previous_step'}
                onChange={(e) => updateConfig('dataSource', e.target.value)}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="previous_step">Előző lépés</option>
                <option value="webhook_data">Webhook adat</option>
                <option value="manual">Manuális</option>
              </select>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Validáció:</span>
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  checked={node.data.config.validateRequired || false}
                  onChange={(e) => updateConfig('validateRequired', e.target.checked)}
                  className="w-3 h-3 text-primary-600" 
                />
                <span className="ml-1">Kötelező mezők</span>
              </label>
            </div>
          </div>

          {/* Action-specific fields */}
          {actionType === 'create_customer' && (
            <div className="mt-3 space-y-2">
              <div>
                <label className={commonStyles.smallLabel}>Ügyfél név</label>
                <input
                  type="text"
                  value={node.data.config.customerName || ''}
                  onChange={(e) => updateConfig('customerName', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.name}} vagy fix név"
                />
              </div>
              <div>
                <label className={commonStyles.smallLabel}>Email</label>
                <input
                  type="email"
                  value={node.data.config.customerEmail || ''}
                  onChange={(e) => updateConfig('customerEmail', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.email}}"
                />
              </div>
              <div>
                <label className={commonStyles.smallLabel}>Telefon</label>
                <input
                  type="text"
                  value={node.data.config.customerPhone || ''}
                  onChange={(e) => updateConfig('customerPhone', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.phone}}"
                />
              </div>
            </div>
          )}

          {actionType === 'update_customer' && (
            <div className="mt-3 space-y-2">
              <div>
                <label className={commonStyles.smallLabel}>Ügyfél ID</label>
                <input
                  type="text"
                  value={node.data.config.customerId || ''}
                  onChange={(e) => updateConfig('customerId', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.id}} vagy fix ID"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  A frissítendő ügyfél egyedi azonosítója
                </p>
              </div>
              <div>
                <label className={commonStyles.smallLabel}>Ügyfél név (opcionális)</label>
                <input
                  type="text"
                  value={node.data.config.customerName || ''}
                  onChange={(e) => updateConfig('customerName', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.name}} vagy új név"
                />
              </div>
              <div>
                <label className={commonStyles.smallLabel}>Email (opcionális)</label>
                <input
                  type="email"
                  value={node.data.config.customerEmail || ''}
                  onChange={(e) => updateConfig('customerEmail', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.email}} vagy új email"
                />
              </div>
              <div>
                <label className={commonStyles.smallLabel}>Telefon (opcionális)</label>
                <input
                  type="text"
                  value={node.data.config.customerPhone || ''}
                  onChange={(e) => updateConfig('customerPhone', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.phone}} vagy új telefon"
                />
              </div>
            </div>
          )}

          {actionType === 'create_project' && (
            <div className="mt-3 space-y-2">
              <div>
                <label className={commonStyles.smallLabel}>Projekt név</label>
                <input
                  type="text"
                  value={node.data.config.projectName || ''}
                  onChange={(e) => updateConfig('projectName', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{project.name}}"
                />
              </div>
              <div>
                <label className={commonStyles.smallLabel}>Ügyfél ID</label>
                <input
                  type="text"
                  value={node.data.config.customerId || ''}
                  onChange={(e) => updateConfig('customerId', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.id}}"
                />
              </div>
            </div>
          )}

          {actionType === 'create_task' && (
            <div className="mt-3 space-y-2">
              <div>
                <label className={commonStyles.smallLabel}>Feladat neve</label>
                <input
                  type="text"
                  value={node.data.config.taskTitle || ''}
                  onChange={(e) => updateConfig('taskTitle', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{task.title}}"
                />
              </div>
              <div>
                <label className={commonStyles.smallLabel}>Leírás</label>
                <textarea
                  value={node.data.config.taskDescription || ''}
                  onChange={(e) => updateConfig('taskDescription', e.target.value)}
                  className={commonStyles.textarea}
                  rows={2}
                  placeholder="{{task.description}}"
                />
              </div>
            </div>
          )}

          {actionType === 'get_customer' && (
            <div className="mt-3 space-y-2">
              <div>
                <label className={commonStyles.smallLabel}>Ügyfél ID</label>
                <input
                  type="text"
                  value={node.data.config.customerId || ''}
                  onChange={(e) => updateConfig('customerId', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.id}} vagy fix ID"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  A lekérdezendő ügyfél egyedi azonosítója
                </p>
              </div>
            </div>
          )}

          {actionType === 'delete_customer' && (
            <div className="mt-3 space-y-2">
              <div>
                <label className={commonStyles.smallLabel}>Ügyfél ID</label>
                <input
                  type="text"
                  value={node.data.config.customerId || ''}
                  onChange={(e) => updateConfig('customerId', e.target.value)}
                  className={commonStyles.input}
                  placeholder="{{customer.id}} vagy fix ID"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  A törlendő ügyfél egyedi azonosítója
                </p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="confirmDelete"
                  checked={node.data.config.confirmDelete || false}
                  onChange={(e) => updateConfig('confirmDelete', e.target.checked)}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label htmlFor="confirmDelete" className="ml-2 text-sm text-red-600 dark:text-red-400">
                  Megerősítem a törlést (VISSZAVONHATATLAN)
                </label>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CRMActionConfig;
